<template>
  <div class="noti-bell">
    <button type="button" class="noti-bell-btn" @click="togglePanel">
      <svg
        class="noti-bell-icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-label="알림"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      <span v-if="unreadCount > 0" class="noti-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
    </button>

    <portal to="modals">
      <div v-show="panelOpen" class="noti-panel-overlay" @click="panelOpen = false"></div>
      <div v-show="panelOpen" class="noti-panel" :style="panelStyle">
        <div class="noti-panel-head">알림</div>
        <ul class="noti-list">
          <li v-if="items.length === 0" class="noti-empty">새 알림이 없습니다.</li>
          <li
            v-for="item in items"
            :key="item.id"
            :class="['noti-item', { unread: !item.readYn }, item.type === 'PAYMENT_REFUND' ? 'type-refund' : 'type-success']"
            @click="onClickItem(item)"
          >
            <p class="noti-title">
              <span class="noti-type-icon">{{ item.type === 'PAYMENT_REFUND' ? '↩' : '💳' }}</span>
              {{ item.title }}
            </p>
            <p class="noti-body">{{ item.body }}</p>
            <p class="noti-date">{{ item.info && item.info.createdAt | timeAgo }}</p>
          </li>
        </ul>
        <button v-if="hasMore" type="button" class="noti-more" @click="loadMore">더 보기</button>
      </div>
    </portal>
  </div>
</template>

<script>
import { getNotiUnreadCount, getNotiList, markNotiAllRead } from '@/assets/js/api';
import { mapGetters } from 'vuex';

const POLL_MS = 60000;
const PAGE_SIZE = 30;

export default {
  name: 'NotificationBell',
  data() {
    return {
      unreadCount: 0,
      items: [],
      page: 1,
      hasMore: false,
      panelOpen: false,
      pollTimer: null,
      panelStyle: { top: '0px', left: '0px', width: '360px' },
    };
  },
  computed: {
    ...mapGetters({ centerId: 'auth/cId' }),
  },
  filters: {
    timeAgo(ts) {
      if (!ts) return '';
      const diff = (Date.now() - new Date(ts).getTime()) / 1000;
      if (diff < 60) return '방금 전';
      if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
      return `${Math.floor(diff / 86400)}일 전`;
    },
  },
  mounted() {
    window.addEventListener('resize', this.onWindowResize);
    if (!this.centerId) return; // 센터 어드민 컨텍스트가 아니면 폴링도 시작하지 않음
    this.fetchUnread();
    this.pollTimer = setInterval(this.fetchUnread, POLL_MS);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
  methods: {
    async fetchUnread() {
      if (!this.centerId) return;
      try {
        const data = await getNotiUnreadCount(this.centerId);
        this.unreadCount = data.count || 0;
      } catch (e) {
        /* 폴링 실패는 조용히 무시(인터셉터가 401 등 처리) */
      }
    },
    async togglePanel() {
      this.panelOpen = !this.panelOpen;
      if (this.panelOpen) {
        this.updatePanelPosition();
        this.page = 1;
        try {
          await this.fetchList();
        } catch (e) {
          console.warn('[NotificationBell] fetchList 실패:', e.message);
        }
        // 목록 수신 후 전체 읽음 → 배지 0
        try {
          await markNotiAllRead(this.centerId);
          this.unreadCount = 0;
        } catch (e) { /* 무시 */ }
      }
    },
    async fetchList() {
      const data = await getNotiList(this.centerId, { page: this.page, limit: PAGE_SIZE });
      this.items = this.page === 1 ? data.items : this.items.concat(data.items);
      this.hasMore = data.meta && data.meta.currentPage < data.meta.totalPages;
    },
    async loadMore() {
      this.page += 1;
      await this.fetchList();
    },
    onClickItem(item) {
      this.panelOpen = false;
      if (item.linkUrl) this.$router.push(item.linkUrl);
    },
    updatePanelPosition() {
      const btn = this.$el && this.$el.querySelector('.noti-bell-btn');
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const vw = window.innerWidth;
      const width = Math.min(360, vw - 16);
      const left = Math.max(8, Math.min(rect.left, vw - width - 8));
      this.panelStyle = { top: `${rect.bottom + 8}px`, left: `${left}px`, width: `${width}px` };
    },
    onWindowResize() {
      if (this.panelOpen) this.updatePanelPosition();
    },
  },
};
</script>

<style scoped>
.noti-bell { position: relative; display: inline-block; }
.noti-bell-btn { position: relative; background: none; border: none; cursor: pointer; }
.noti-bell-icon { width: 22px; height: 22px; }
.noti-badge {
  position: absolute; top: -6px; right: -8px; min-width: 16px; height: 16px;
  padding: 0 4px; border-radius: 8px; background: #f5475b; color: #fff;
  font-size: 10px; line-height: 16px; text-align: center;
}
.noti-panel-overlay { position: fixed; inset: 0; z-index: 9998; }
.noti-panel {
  position: fixed; max-height: 70vh; overflow-y: auto;
  background: #fff; border: 1px solid #e0e0e0; border-radius: 8px; z-index: 9999;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}
.noti-panel-head { padding: 12px 16px; font-weight: 600; border-bottom: 1px solid #eee; }
.noti-list { list-style: none; margin: 0; padding: 0; }
.noti-item { padding: 12px 16px; border-bottom: 1px solid #f2f2f2; cursor: pointer; }
.noti-item.unread { background: #f7fbff; }
.noti-title { font-weight: 600; margin: 0 0 2px; }
.noti-body { color: #555; margin: 0 0 2px; font-size: 13px; }
.noti-date { color: #999; margin: 0; font-size: 11px; }
.noti-empty { padding: 24px 16px; text-align: center; color: #999; }
.noti-more { width: 100%; padding: 10px; border: none; border-top: 1px solid #eee; background: #fafafa; cursor: pointer; }
</style>
